import { Drawer, Tab, Tabs } from '@material-ui/core';
import * as React from 'react';
import { useStore } from 'react-redux';
import { Link } from 'react-router-dom';
import { uuid } from 'uuidv4';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { FlyoutTitle } from '../../../../components/flyout-components/FlyoutTitle';
import { LoadingPanel } from '../../../../components/loading/Loading';
import { StickyActionBar } from '../../../../components/sticky-action-bar/StickyActionBar';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { AppliedFilterBarOpt, IFilterBar } from '../../../../models/utilities/filter-bar';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { getServiceCenterTimezone } from '../../../../selectors/utility-selectors';
import { StatusCode } from '../../../../utils/api';
import { Routes } from '../../../../utils/app-constants';
import { TransportationUtils } from '../../../../transportation/helpers';
import { ContainerRead } from '../../../../transportation/models/containers/container-read';
import { ShipmentActionType } from '../../../../transportation/models/enums/shipment-action-type';
import { ShipmentMethod } from '../../../../transportation/models/enums/shipment-method';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';
import { DELIVERABLE_SHIPMENT_STATUS, OrderSearchFilters } from '../../../../transportation/models/filters/order-search-filters';
import { UnplannedRouteGroup } from '../../../../transportation/models/planning/unplanned-route-group';
import { OrderSummary } from '../../../../transportation/models/shared/order-summary';
import { RunningTotal } from '../../../../transportation/models/shared/running-total';
import { DeliveryShipmentAction, EndShipmentAction, ShipmentAction, StartShipmentAction } from '../../../../transportation/models/shipments/shipment-action';
import { ShipmentRead } from '../../../../transportation/models/shipments/shipment-read';
import { TruckRead } from '../../../../transportation/models/trucks/truck-read';
import { TransportationSvc } from '../../../../transportation/service';
import { ContainerEditBasicInfo } from '../../../../transportation/components/shipments/ContainerEditBasicInfo';
import { ShipmentEditCustomerDelivery } from '../../../../transportation/components/shipments/ShipmentEditCustomerDelivery';
import { ShipmentEditDestination } from '../../../../transportation/components/shipments/ShipmentEditDestination';
import { ShipmentEditOrigin } from '../../../../transportation/components/shipments/ShipmentEditOrigin';
import { CustomerDeliverShipmentTab, DestinationShipmentTab, OriginShipmentTab } from '../../../../transportation/components/shipments/ShipmentTab';
import { TruckEditBasicInfo } from '../../../../transportation/components/shipments/TruckEditBasicInfo';

interface IProps {
    visible             : boolean;
    onSuccess           : any;
    onClose             : any;
    selectedAddresses   : UnplannedRouteGroup[];
    entityType          : ShipmentType;
    filters             : IFilterBar;
}

interface ITabPanelProps {
    children?   : React.ReactNode;
    index       : any;
    value       : any;
}

const TabPanel = (props: ITabPanelProps) => {
    const { children, value, index } = props;
    return value === index 
            ? <div className="truck-delivery-row">{children}</div> 
            : null;
};

export const RoutingRouteCreate = (props: IProps) => {

    const store                                 = useStore();
    const [selectedRoute, setSelectedRoute]     = React.useState<number>(0);        
    const userServiceCenter                     = useServiceCenter();
    const [service]                             = React.useState(new TransportationSvc());
    const [editRoute, setEditRoute]             = React.useState<ShipmentRead>(new ShipmentRead());
    const [newEntityId, setNewEntityId]         = React.useState(0);
    const [isFetching, setIsFetching]           = React.useState(true);
    const [fullTruckTotal, setFullTruckTotal]   = React.useState<RunningTotal>(new RunningTotal());

    const onSelectedRouteChange = (event: React.ChangeEvent<{}>, newIdx: any) => {
        setSelectedRoute(newIdx);

        const r = {...editRoute};       
        const tmpTotal = rollUpOtherStops(r, ''); 
        setFullTruckTotal(tmpTotal);
    };

    async function prefetchOrders() {
        
        const activeSC = userServiceCenter.name === "ALL" ? "MAIN" : userServiceCenter.name;
        const scTimezone = getServiceCenterTimezone(store.getState(), activeSC);

        const newRoute = props.entityType === ShipmentType.Truck ? new TruckRead() : new ContainerRead();
        newRoute.serviceCenterId = activeSC;

        const dateFilter = props.filters.appliedFilters.find((itm: AppliedFilterBarOpt) => {
            return itm.qsName === 'maxCutoff';
        });

        if (dateFilter) {
            // "2021-03-19&matchDayOfWeek=true"
            const maxDate = dateFilter.qsValue.split('&')[0];
            const t = new Date(maxDate + 'T12:00:00.000Z');
            newRoute.cutoffDate = t;
        }
        
        const orig = new StartShipmentAction();
        orig.address.timeZoneId = scTimezone;        
        orig.actionTime = undefined;
        orig.tmpClientId = uuid();

        newRoute.actions.push(orig);

        let tmpTotals = new RunningTotal();

        for (let i = 0; i < props.selectedAddresses.length; i++) {
            const stop = props.selectedAddresses[i];
            
            const cd            = new DeliveryShipmentAction();
            cd.dealerId         = stop.dealerId;
            cd.shipToAddressId  = stop.shipToAddressId;
            cd.address          = stop.deliveryAddress;
            cd.actionTime         = undefined;
            cd.tmpClientId      = uuid();
            cd.orderGroupingId  = stop.orderGroupId;

            const filters = getOrderQueryFilter(stop.shipToAddressId, stop.orderGroupId);
            const response = await service.getOrdersForShipTo(filters);

            if (response.status === StatusCode.Success) {
                cd.orders = response.data.results;
            }

            newRoute.actions.push(cd);
            tmpTotals = TransportationUtils.calculateTruckTotals(tmpTotals, cd.orders, []);
        }

        if (props.entityType === ShipmentType.Truck) {
            const dest = new EndShipmentAction();
            dest.address.timeZoneId = scTimezone;
            dest.actionTime = undefined;
            dest.tmpClientId = uuid();

            newRoute.actions.push(dest);
        }

        setFullTruckTotal(tmpTotals);
        setEditRoute(newRoute);
        setIsFetching(false);
    }

    /**
     * Prefetch the orders for the stops on the route when 
     * it becomes visible
     */
    React.useEffect(() => {
        if (props.visible) {    
            setIsFetching(true);
            prefetchOrders();
        }
    }, [props.visible]);

    /**
     * Handle the route changing in the children controls
     * @param newRoute The new route object to update to
     */
    const handleOnChange = (newRoute : ShipmentRead) => {
        if (newRoute) {
            setEditRoute({...newRoute});
        }
    };

    /**
     * Save the changes to the route before closing
     */ 
    const onSaveInfo = async () => {
        if (editRoute) {            
            const result = await service.saveRoute(editRoute, true);
            if (result.status === StatusCode.Success) {
                setNewEntityId(result.data as number);
            }
        }
    };

    /**
     * Cleanup some our state and close the panel
     */
    const onClose = () => {        
        
        (newEntityId > 0) ? props.onSuccess() : props.onClose();

        setEditRoute(new ShipmentRead());
        setNewEntityId(0);
        
    };
    
    const onUpdateStopOrders = (item : PagedApiResult<OrderSummary>, tmpId : string) => {
        
        const r = {...editRoute};
        const tmpStop = r.actions.find((itm : DeliveryShipmentAction) => {
            return itm.tmpClientId === tmpId;
        }) as DeliveryShipmentAction;

        tmpStop.orders = item.results;
        tmpStop.wasSeen = true;
        
        setEditRoute(r);

        let tmpTotal = rollUpOtherStops(r, tmpId);
        tmpTotal = TransportationUtils.calculateTruckTotals(tmpTotal, tmpStop.orders, []);
        setFullTruckTotal(tmpTotal);
    };

    const onUpdateStop = (cd : DeliveryShipmentAction, tmpId : string) => {

        const r = {...editRoute};
        const stopIdx = r.actions.findIndex((itm : DeliveryShipmentAction) => {
            return itm.tmpClientId === tmpId;
        });

        cd.wasSeen = true;
        r.actions[stopIdx] = cd;

        setEditRoute(r);
    };

    const onTotalUpdate = (onDragTotals : RunningTotal, tmpId : string) => {

        const r = {...editRoute};
       
        const tmpTotal = rollUpOtherStops(r, tmpId); 
        tmpTotal.units     += onDragTotals.units;
        tmpTotal.weight    += onDragTotals.weight;
        tmpTotal.singles   += onDragTotals.singles;
        tmpTotal.sections  += onDragTotals.sections;
        tmpTotal.track     += onDragTotals.track;
        tmpTotal.hardware  += onDragTotals.hardware;

        setFullTruckTotal(tmpTotal);        
    };

    const rollUpOtherStops = (r : ShipmentRead, tmpId: string) => {
        let tmpTotal = new RunningTotal();
        
        // Filter out this stop from the main list
        r.actions.filter((itm : ShipmentAction) => {
            return (itm.type === ShipmentActionType.Delivery) && (itm.tmpClientId !== tmpId);
        }).forEach((itm : DeliveryShipmentAction) => {
            tmpTotal = TransportationUtils.calculateTruckTotals(tmpTotal, itm.orders, []);
        });

        return tmpTotal;
    };

    /**
     * Updated the origin of the route
     * @param dest The origin stop of the truck
     * @param tmpId The client ID for matching
     */
    const onOriginUpdate = (dest : StartShipmentAction, tmpId : string) => {
        const r = {...editRoute} as ShipmentRead;
        r.actions[0] = dest;
        setEditRoute(r);
    };

    /**
     * Updated the destination of the route
     * @param dest The destination stop of the truck
     * @param tmpId The client ID for matching
     */
    const onDestinationUpdate = (dest : EndShipmentAction, tmpId : string) => {
        const r = {...editRoute} as ShipmentRead;
        r.actions[r.actions.length - 1] = dest;
        setEditRoute(r);
    };

    /**
     * Render the tab for the provided stop
     * @param itm The route to render
     * @param index The index for keying
     */
    const getTabContent = (itm : ShipmentAction, index : number) => {    

        if (itm.type === ShipmentActionType.Unknown) {
            return null;
        }

        switch (itm.type) {
            case ShipmentActionType.Delivery:
                return <CustomerDeliverShipmentTab 
                            index={ index } 
                            delivery={ itm as DeliveryShipmentAction }
                        />;
            case ShipmentActionType.Origin:
                return <OriginShipmentTab />;
            case ShipmentActionType.Destination:
                return <DestinationShipmentTab />;
            default: 
                return null;
        }
    };

    /**
     * Render the tab contnet panel for the provided stop
     * @param itm The route to render
     * @param index The index for keying
     */
    const getTabPanelContent = (itm : ShipmentAction, index : number) => {
        if (itm.type === ShipmentActionType.Unknown) {
            return null;
        }

        switch (itm.type) {
            case ShipmentActionType.Delivery:
                return _getCustomerDeliveryPanel(itm as DeliveryShipmentAction);
            case ShipmentActionType.Origin:
                return <ShipmentEditOrigin 
                            origin={ itm as StartShipmentAction } 
                            isQuickCreate={ true }
                            updateTruck={ onOriginUpdate }
                        />;
            case ShipmentActionType.Destination:
                return <ShipmentEditDestination 
                            destination={ itm as EndShipmentAction } 
                            isQuickCreate={ true }
                            updateTruck={ onDestinationUpdate }
                        />;
            default: 
                return null;
        }
    };

    const _getCustomerDeliveryPanel = (itm : DeliveryShipmentAction) => {

        const f = getOrderQueryFilter((itm as DeliveryShipmentAction).shipToAddressId, (itm as DeliveryShipmentAction).orderGroupingId);
        const activeSC = userServiceCenter.name === "ALL" ? "MAIN" : userServiceCenter.name;
        return <ShipmentEditCustomerDelivery
                    stop={ itm as DeliveryShipmentAction }
                    orderList={ undefined }
                    isQuickCreate={ true }
                    onSave={ undefined }
                    onCancel={ undefined }
                    changeAddress={ undefined }
                    updateStops={ onUpdateStopOrders }
                    updateTruck={ onUpdateStop }
                    updateTotals={ onTotalUpdate }
                    entityType={ props.entityType }
                    planningFilters={ f }
                    visible={ true }
                    truckId={ -1 }
                    truckVersion={ "" }
                    truckTotals={undefined}
                    serviceCenterId={activeSC}
                />;

    };

    const getOrderQueryFilter = (addressId : number, groupId : number) => {
        const dateFilter = props.filters.appliedFilters.find((itm: AppliedFilterBarOpt) => {
            return itm.qsName === 'maxCutoff';
        });

        const dealerFilter = props.filters.appliedFilters.find((itm: AppliedFilterBarOpt) => {
            return itm.qsName === 'dealerId';
        });

        const sourceFilter = props.filters.appliedFilters.find((itm: AppliedFilterBarOpt) => {
            return itm.qsName === 'source';
        });

        const scFilter = userServiceCenter.name === "ALL" ? "MAIN" : userServiceCenter.name;

        const filters = new OrderSearchFilters(
            ShipmentMethod.Delivery,
            addressId,
            dateFilter?.qsValue,
            dealerFilter?.qsValue,
            sourceFilter?.qsValue,
            groupId,
            scFilter,
            undefined,
            DELIVERABLE_SHIPMENT_STATUS
        );

        return filters;
    };

    return <Drawer 
                anchor="right" 
                onClose={ onClose }
                open={ props.visible }>
                <div className={`stop-action-drawer large planning-create ${ newEntityId > 0 ? 'shrink' : '' }`}>
                    {
                        editRoute && newEntityId === 0
                            ? <React.Fragment>

                                { 
                                    isFetching 
                                        ? <LoadingPanel blockLevel={true} /> 
                                        : null
                                }

                                {
                                    props.entityType === ShipmentType.Truck
                                        ? <React.Fragment>
                                            <FlyoutTitle
                                                onMobileClose={ onClose }
                                                title="Create New Truck" />                                            
                                            <TruckEditBasicInfo
                                                truck={ editRoute as TruckRead }
                                                showCompactUI={ false }
                                                onTruckChange={ handleOnChange }
                                            />
                                        </React.Fragment>
                                        : <React.Fragment>
                                            <FlyoutTitle 
                                                onMobileClose={ onClose }
                                                title="Create New Container" />                                                      
                                            <ContainerEditBasicInfo
                                                container={ editRoute as ContainerRead }
                                                showCompactUI={ false }
                                                allowCarrierSet={ true }
                                                onContainerChange={ handleOnChange } />
                                        </React.Fragment>
                                }

                                <div className="truck-stop-route-tab-wrapper">
                                    <Tabs
                                        orientation="vertical"
                                        value={ selectedRoute }
                                        onChange={ onSelectedRouteChange }
                                        classes={{ flexContainer: 'truck-stop-route-tab-list' }}
                                        aria-label="Route Stops">
                                            {
                                                editRoute.actions.map((itm: ShipmentAction, idx : number) => 
                                                    <Tab 
                                                        classes={{ root: 'truck-stop-route-tab-item' }} 
                                                        key={ `tab-itm-${ idx }` }
                                                        label={ getTabContent(itm, idx) } 
                                                    />
                                                )
                                            }
                                    </Tabs>
                                    {
                                        editRoute.actions.map((itm: ShipmentAction, idx : number) => {
                                            return <TabPanel 
                                                key={ `tab-panel-${ idx }` }
                                                value={selectedRoute} 
                                                index={idx}>
                                                    { getTabPanelContent(itm, idx) }
                                            </TabPanel>;
                                        })
                                    }
                                </div>

                                <StickyActionBar 
                                    classes="planning-create-totals"
                                    showBar={ true }>
                                    
                                    <div className="running-total-bar">
                                        <p className="row-label">
                                            {
                                                props.entityType === ShipmentType.Truck
                                                    ? 'Truck Totals'
                                                    : 'Container Totals'
                                            }                                            
                                        </p>
                                        <div>
                                            <p className={`rollup-metric ${ fullTruckTotal.getUnitWarning() }`}>
                                                <span>Units</span>
                                                <span>{ fullTruckTotal.units}</span>
                                            </p>
                                            <p className={`rollup-metric ${ fullTruckTotal.getWeightWarning() }`}>
                                                <span>Weight</span>
                                                <span>{ fullTruckTotal.weight}</span>
                                            </p>
                                            <p className="rollup-metric">
                                                <span>Singles</span>
                                                <span>{ fullTruckTotal.singles}</span>
                                            </p>
                                            <p className="rollup-metric">
                                                <span>Sections</span>
                                                <span>{ fullTruckTotal.sections}</span>
                                            </p>
                                            <p className="rollup-metric">
                                                <span>Track</span>
                                                <span>{ fullTruckTotal.track}</span>
                                            </p>
                                            <p className="rollup-metric">
                                                <span>Hardware</span>
                                                <span>{ fullTruckTotal.hardware}</span>
                                            </p>
                                        </div>                            
                                    </div>
                                </StickyActionBar>
        
                                <FlyoutSaveCancel
                                    onSave={ onSaveInfo }
                                    onCancel={ onClose }
                                />

                            </React.Fragment>
                            : <React.Fragment>
                                <FlyoutTitle    
                                    onMobileClose={ onClose }
                                    title="Success!" />
                                    <div className="row">
                                        {
                                            props.entityType === ShipmentType.Truck
                                                ? <div className="col">
                                                    <h3>Truck Created Successfully!</h3>
                                                    <p>Click below to head to the details.</p>
                                                    <Link to={ Routes.SHIPPING_TRUCK_EDIT_WITH_ID.formattify(newEntityId) }>
                                                        View Details
                                                    </Link>
                                                </div>
                                                : <div className="col">
                                                    <h3>Container Created Successfully!</h3>
                                                    <p>Click below to head to the details.</p>
                                                    <Link to={ Routes.SHIPPING_CONTAINER_EDIT_WITH_ID.formattify(newEntityId) }>
                                                        View Details
                                                    </Link>
                                                </div>
                                        }                                        
                                    </div>
                            </React.Fragment>
                    }
                </div>
            </Drawer>;
};
